import React from "react";

export function formatDate(iso, opts = {}) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", ...opts }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat("pt-PT", { numeric: "auto" });
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), "minute");
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), "hour");
  if (diff < 86400 * 30) return rtf.format(-Math.round(diff / 86400), "day");
  return formatDate(iso, { hour: undefined, minute: undefined });
}

const INLINE_RE = /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|`[^`]+`|\*[^*\n]+\*|_[^_\n]+_|https?:\/\/[^\s<>)]+|<a?:\w+:\d+>|<@!?\d+>|<@&\d+>|<#\d+>|<t:\d+(?::[tTdDfFR])?>|\|\|[^|]+\|\|)/g;

function renderInline(text, keyPrefix) {
  const parts = text.split(INLINE_RE).filter((p) => p !== "" && p !== undefined);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={key} className="text-eden-text">{renderInline(part.slice(2, -2), key)}</strong>;
    if (/^__[^_]+__$/.test(part)) return <u key={key}>{renderInline(part.slice(2, -2), key)}</u>;
    if (/^~~[^~]+~~$/.test(part)) return <s key={key}>{renderInline(part.slice(2, -2), key)}</s>;
    if (/^`[^`]+`$/.test(part)) return <code key={key}>{part.slice(1, -1)}</code>;
    if (/^\|\|[^|]+\|\|$/.test(part)) return <span key={key} className="rounded bg-eden-elevated px-1 text-eden-muted">{part.slice(2, -2)}</span>;
    if (/^\*[^*\n]+\*$/.test(part) || /^_[^_\n]+_$/.test(part)) return <em key={key}>{renderInline(part.slice(1, -1), key)}</em>;
    if (/^https?:\/\//.test(part)) {
      const clean = part.replace(/[.,;!?]+$/, "");
      return <a key={key} href={clean} target="_blank" rel="noopener noreferrer">{clean.replace(/^https?:\/\//, "").slice(0, 60)}</a>;
    }
    if (/^<a?:\w+:\d+>$/.test(part)) return <span key={key} className="text-eden-muted">{`:${part.split(":")[1]}:`}</span>;
    if (/^<@!?\d+>$/.test(part)) return <span key={key} className="text-eden-turquoise">@membro</span>;
    if (/^<@&\d+>$/.test(part)) return <span key={key} className="text-eden-turquoise">@cargo</span>;
    if (/^<#\d+>$/.test(part)) return <span key={key} className="text-eden-turquoise">#canal</span>;
    if (/^<t:\d+/.test(part)) {
      const unix = Number(part.match(/\d+/)[0]);
      return <time key={key} dateTime={new Date(unix * 1000).toISOString()}>{formatDate(new Date(unix * 1000).toISOString())}</time>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

export function DiscordText({ text, className = "" }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="my-1 list-disc space-y-0.5 pl-5">{list}</ul>);
      list = [];
    }
  };
  lines.forEach((line, i) => {
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    const li = line.match(/^\s*[-*•]\s+(.*)$/);
    const quote = line.match(/^>\s?(.*)$/);
    if (h) {
      flushList();
      const Tag = h[1].length === 1 ? "h4" : "h5";
      blocks.push(<Tag key={i} className={h[1].length === 1 ? "mt-2 font-display text-lg font-semibold text-eden-text" : "mt-2 font-display text-base font-semibold text-eden-text"}>{renderInline(h[2], `h${i}`)}</Tag>);
    } else if (li) {
      list.push(<li key={i}>{renderInline(li[1], `li${i}`)}</li>);
    } else if (quote) {
      flushList();
      blocks.push(<blockquote key={i}>{renderInline(quote[1], `q${i}`)}</blockquote>);
    } else if (line.trim() === "") {
      flushList();
      blocks.push(<div key={i} className="h-2" aria-hidden="true" />);
    } else {
      flushList();
      blocks.push(<p key={i}>{renderInline(line, `p${i}`)}</p>);
    }
  });
  flushList();
  return <div className={`prose-discord text-sm leading-relaxed text-eden-secondary ${className}`}>{blocks}</div>;
}
